import { __spreadArray } from 'tslib';
import { useLocalization } from '@shared-ui/localization-context';
import * as React from 'react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkLineSkeleton } from 'uitk-react-skeleton';
import { UitkSpacing } from 'uitk-react-spacing';
import { classNames, liveAnnounce, Level } from 'uitk-react-utils';
import styles from './uitk-date-range-grid.css';
import { UitkDateRangeGridCellTheme, UitkDateRangeGridHeaderTheme } from './types.esm.js';
import { UitkDateRangeGridCell } from './uitk-date-range-grid-cell.esm.js';
import { UitkDateRangeGridGetSelectedDateScrollCenterDistance } from './uitk-date-range-grid-util-functions.esm.js';

var getClassNameList = classNames.bind(styles);
var UitkDateRangeGrid = function (_a) {
    var _b, _c;
    var a11yTitle = _a.a11yTitle, a11ySelectedText = _a.a11ySelectedText, columnLegend = _a.columnLegend, loading = _a.loading, results = _a.results, rowLegend = _a.rowLegend;
    var initSelectedCell = results === null || results === void 0 ? void 0 : results.map(function (row, rowIndex) {
        var cellIndex = row.findIndex(function (cell) { return cell && 'theme' in cell && cell.theme === UitkDateRangeGridCellTheme.SELECTED_CELL; });
        return cellIndex === -1 ? undefined : { rowIndex: rowIndex, cellIndex: cellIndex };
    }).filter(Boolean)[0];
    var l10n = useLocalization();
    var _d = useState(results), contentArray = _d[0], setContentArray = _d[1];
    var _e = useState(initSelectedCell || { rowIndex: 0, cellIndex: 0 }), selectedCellData = _e[0], setSelectedCellData = _e[1];
    var dateRangeGridRef = useRef(null);
    var scrollMouse = { move: false, initialMousePos: 0, buttonLeft: 0 };
    var numberOfEmptyRowHeaders = (results === null || results === void 0 ? void 0 : results.length) ? results.length - 3 : 0;
    var defaultEmptyColHeaders = 7;
    var _f = useState(''), selectedCellTheme = _f[0], saveSelectedCellTheme = _f[1];
    var centerGridOnSelectedDate = function () {
        var dateRangeGridEl = dateRangeGridRef === null || dateRangeGridRef === void 0 ? void 0 : dateRangeGridRef.current;
        var selectedDateScrollCenterDistance = UitkDateRangeGridGetSelectedDateScrollCenterDistance(dateRangeGridEl);
        dateRangeGridEl && (dateRangeGridEl.scrollLeft = selectedDateScrollCenterDistance);
    };
    useEffect(function () {
        setTimeout(function () {
            setContentArray(results);
        }, 0);
    }, [results]);
    useEffect(function () {
        setTimeout(function () {
            centerGridOnSelectedDate();
        }, 0);
    });
    useEffect(function () {
        liveAnnounce(''); // Set up empty live announce region
    }, []);
    var mouseDown = function (event) {
        scrollMouse.move = true;
        scrollMouse.initialMousePos = event.clientX;
        scrollMouse.buttonLeft = event.currentTarget.scrollLeft;
    };
    var mouseUp = function () {
        scrollMouse.move = false;
    };
    var mouseMove = function (event) {
        if (scrollMouse.move) {
            // eslint-disable-next-line no-param-reassign
            event.currentTarget.scrollLeft = scrollMouse.buttonLeft + scrollMouse.initialMousePos - event.clientX;
        }
    };
    var handleCellButtonClick = useCallback(function (rowIndex, cellIndex) {
        var newSelectedCell = { rowIndex: rowIndex, cellIndex: cellIndex };
        /* Instead of disabling the button, check if the same cell has been selected and return (assists a11y) */
        if (JSON.stringify(newSelectedCell) === JSON.stringify(selectedCellData)) {
            return;
        }
        var contentArrayClone = JSON.parse(JSON.stringify(contentArray)); // Deep Clone
        var selectRowArrayLength = contentArrayClone[selectedCellData === null || selectedCellData === void 0 ? void 0 : selectedCellData.rowIndex].length;
        var newlySelectedRowArrayLength = contentArrayClone[rowIndex].length;
        /* Unselect previously selected */
        contentArrayClone[0][selectedCellData === null || selectedCellData === void 0 ? void 0 : selectedCellData.cellIndex].headerTheme = UitkDateRangeGridHeaderTheme.DEFAULT;
        contentArrayClone[selectedCellData === null || selectedCellData === void 0 ? void 0 : selectedCellData.rowIndex][selectRowArrayLength - 1].headerTheme =
            UitkDateRangeGridHeaderTheme.DEFAULT;
        contentArrayClone[selectedCellData === null || selectedCellData === void 0 ? void 0 : selectedCellData.rowIndex][selectedCellData === null || selectedCellData === void 0 ? void 0 : selectedCellData.cellIndex].theme = selectedCellTheme;
        /* Add selected state to newly selected cell */
        contentArrayClone[0][cellIndex].headerTheme = UitkDateRangeGridHeaderTheme.SELECTED_HEADER;
        contentArrayClone[rowIndex][newlySelectedRowArrayLength - 1].headerTheme =
            UitkDateRangeGridHeaderTheme.SELECTED_HEADER;
        saveSelectedCellTheme(contentArrayClone[rowIndex][cellIndex].theme);
        contentArrayClone[rowIndex][cellIndex].theme = UitkDateRangeGridCellTheme.SELECTED_CELL;
        setContentArray(contentArrayClone);
        setSelectedCellData(newSelectedCell);
        liveAnnounce("".concat(contentArrayClone[rowIndex][cellIndex].accessibilityText, " ").concat(a11ySelectedText !== null && a11ySelectedText !== void 0 ? a11ySelectedText : ''), Level.POLITE);
    }, [contentArray, selectedCellData, a11ySelectedText, selectedCellTheme]);
    var renderEmptyHeaders = function (val) {
        if (val === void 0) { val = numberOfEmptyRowHeaders; }
        // @ts-ignore
        return __spreadArray([], Array(val), true).map(function (_, i) { return React.createElement("th", { key: "empty-header-".concat(i), "aria-hidden": "true" }); });
    };
    var renderColumnHeaders = function () {
        var columnHeadersArray = contentArray[0];
        return columnHeadersArray.map(function (headerObj, index) {
            if (!headerObj || index > 6) {
                return;
            }
            var isSelectedHeader = headerObj.headerTheme === UitkDateRangeGridHeaderTheme.SELECTED_HEADER;
            var selectedColumnClass = getClassNameList({
                'selected-header': isSelectedHeader
            });
            var headerContent = headerObj.dayText && headerObj.dateText
                ? "".concat(l10n.formatText('dateflexiblegrid.flexibleDate', headerObj.dayText, headerObj.dateText))
                : '';
            return (React.createElement("th", { key: "column-date-".concat(index), className: selectedColumnClass, "aria-hidden": "true" }, headerContent));
        });
    };
    var getClassNames = function (cell, rowIndex, cellIndex) {
        var _a;
        var isSelectedColCell = cellIndex === (selectedCellData === null || selectedCellData === void 0 ? void 0 : selectedCellData.cellIndex) && rowIndex < (selectedCellData === null || selectedCellData === void 0 ? void 0 : selectedCellData.rowIndex);
        var isSelectedRowCell = rowIndex === (selectedCellData === null || selectedCellData === void 0 ? void 0 : selectedCellData.rowIndex) && cellIndex > (selectedCellData === null || selectedCellData === void 0 ? void 0 : selectedCellData.cellIndex);
        return getClassNameList((_a = {
                'frozen-col': cellIndex === 7,
                'is-disabled': cell.theme === 'unavailable' || cell.theme === 'selected-cell',
                'selected-cell': cell.theme === 'selected-cell',
                'selected-column': isSelectedColCell,
                'selected-header': cell.headerTheme === UitkDateRangeGridHeaderTheme.SELECTED_HEADER,
                'selected-row': isSelectedRowCell
            },
            _a["uitk-date-range-grid-".concat(cell.theme)] = cell.theme,
            _a));
    };
    var createTableRow = function (transformedTableBodyRow, rowIndex) {
        return transformedTableBodyRow.map(function (cell, cellIndex) {
            var dateRangeGridclassNames = getClassNames(cell, rowIndex, cellIndex);
            var isSelectedCell = cell.theme === 'selected-cell';
            if ('priceText' in cell) {
                return (React.createElement("td", { key: "table-cell-".concat(cellIndex), className: dateRangeGridclassNames },
                    React.createElement(UitkDateRangeGridCell, { result: cell, onClickCB: function () { return handleCellButtonClick(rowIndex, cellIndex); }, "aria-disabled": isSelectedCell || cell.theme === 'unavailable' || undefined, "aria-label": "".concat(cell.accessibilityText, " ").concat(isSelectedCell ? a11ySelectedText !== null && a11ySelectedText !== void 0 ? a11ySelectedText : '' : ''), className: getClassNames(cell, rowIndex, cellIndex) })));
            }
            return (React.createElement("th", { key: "table-column-".concat(rowIndex), className: dateRangeGridclassNames, scope: "row", "aria-hidden": "true" }, l10n.formatText('dateflexiblegrid.flexibleDate', cell.dayText, cell.dateText)));
        });
    };
    var handleTableBody = function () {
        return contentArray.reduce(function (mappedArray, tableBodyRow, rowIndex) {
            if (rowIndex !== 0) {
                var tableRowsArray = createTableRow(tableBodyRow, rowIndex);
                mappedArray.push((React.createElement("tr", { key: "table-row-".concat(rowIndex) }, tableRowsArray)));
            }
            return mappedArray;
        }, []);
    };
    var createLoadingRow = function (row, rowIndex) {
        return row.map(function (_, cellIndex) {
            return cellIndex < defaultEmptyColHeaders ? (React.createElement("td", { key: "loading-column-".concat(cellIndex, "-row-").concat(rowIndex), className: getClassNameList('no-hover'), "aria-hidden": "true" },
                React.createElement(UitkSpacing, { padding: { inline: 'six' } },
                    React.createElement("div", null,
                        React.createElement(UitkLineSkeleton, null))))) : (React.createElement("th", { key: "loading-return-column-".concat(rowIndex), className: getClassNameList('frozen-col'), scope: "row", "aria-hidden": "true" }));
        });
    };
    var handleLoadingBody = function () {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        var arr = __spreadArray([], Array(defaultEmptyColHeaders), true).map(function (_) { return __spreadArray([], Array(defaultEmptyColHeaders + 1), true); });
        return arr.map(function (row, rowIndex) {
            var rowsArray = createLoadingRow(row, rowIndex);
            return React.createElement("tr", { key: "table-row-".concat(rowIndex, "-loading") }, rowsArray);
        });
    };
    var caption = (React.createElement("caption", { className: getClassNameList('uitk-date-range-grid-table-caption', 'is-visually-hidden') }, a11yTitle));
    return (React.createElement("div", { className: getClassNameList('uitk-date-range-grid'), ref: dateRangeGridRef },
        React.createElement("table", { className: getClassNameList('uitk-date-range-grid-table'), role: "table", onMouseDown: mouseDown, onMouseUp: mouseUp, onMouseMove: mouseMove, onMouseLeave: mouseUp },
            !loading && (contentArray === null || contentArray === void 0 ? void 0 : contentArray.length) && (React.createElement(React.Fragment, null,
                caption,
                React.createElement("thead", null,
                    React.createElement("tr", null,
                        React.createElement(UitkSpacing, { margin: { block: 'three' }, padding: { inline: 'three' } },
                            React.createElement("th", { className: getClassNameList('legend-cell', 'x-axis', 'frozen-row'), "aria-hidden": "true", colSpan: 2 }, columnLegend === null || columnLegend === void 0 ? void 0 :
                                columnLegend.labelText,
                                React.createElement(UitkSpacing, { padding: { inline: 'one' } },
                                    React.createElement(UitkIcon, { name: (_b = columnLegend === null || columnLegend === void 0 ? void 0 : columnLegend.icon) === null || _b === void 0 ? void 0 : _b.id, size: UitkIconSize.EXTRA_SMALL })))),
                        renderEmptyHeaders(),
                        React.createElement(UitkSpacing, { padding: { block: 'two' } },
                            React.createElement("th", { className: getClassNameList('legend-cell', 'y-axis', 'frozen-col'), rowSpan: 2, scope: "col", "aria-hidden": "true" }, rowLegend === null || rowLegend === void 0 ? void 0 :
                                rowLegend.labelText,
                                React.createElement(UitkSpacing, { padding: { inline: 'one' } },
                                    React.createElement(UitkIcon, { name: (_c = rowLegend === null || rowLegend === void 0 ? void 0 : rowLegend.icon) === null || _c === void 0 ? void 0 : _c.id, size: UitkIconSize.EXTRA_SMALL }))))),
                    React.createElement("tr", null, renderColumnHeaders())),
                React.createElement("tbody", { onMouseDown: mouseDown, onMouseUp: mouseUp, onMouseMove: mouseMove, onMouseLeave: mouseUp }, handleTableBody()))),
            loading && (React.createElement(React.Fragment, null,
                caption,
                React.createElement("thead", null,
                    React.createElement("tr", null,
                        renderEmptyHeaders(defaultEmptyColHeaders),
                        React.createElement("th", { className: getClassNameList('legend-cell', 'y-axis', 'frozen-col'), rowSpan: 2, scope: "col", "aria-hidden": "true" })),
                    React.createElement("tr", null, renderEmptyHeaders(defaultEmptyColHeaders))),
                React.createElement("tbody", null, handleLoadingBody()))))));
};

export { UitkDateRangeGrid };
